import { NgxSpinnerService } from "ngx-spinner";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { HttpClient, HttpHeaders } from "@angular/common/http";

import { IFilter } from "../../models/shared/ifilter";
import { IApi } from "../../models/shared/iapi";

import { IBaseResponseResult } from "../../models/shared/ibase-response-result";
import { BaseResponseResult } from "../../models/shared/base-response-result";
import { ResponseResult } from "../../models/shared/response-result";
import { InformationResult } from "../../models/shared/information-result";
import { ImageParameters } from "../../models/shared/image-parameters";
import { EventEmitter } from "@angular/core";
import { ResponseCache } from "../../models/shared/response-cache";
import { Error } from "../../models/shared/error";
import * as moment from "moment";
import { Subscription } from "rxjs";

enum Method {
  GET,
  POST,
  DELETE,
  PUT,
  PATCH,
  UPLOAD,
  DOWNLOAD,
}

export class BaseDataService<E, F extends IFilter<E>, A extends IApi<E>> {
  public CurrentChanged$: EventEmitter<E | A>;
  public CurrentsChanged$: EventEmitter<(E | A)[]>;
  public EndPoint: string;
  public Domain = "api";
  public Username = "";
  public Password = "1!ga8xb$j&k";
  public CustomHeaders: { Name: string; Value: string }[] = [];
  public StoreKey: string;
  public FromCache = false;
  public CurrentCall: Array<{
    Type: string;
    Value: Subscription;
    Loader: boolean;
  }> = [];
  public UseCache = false;

  get Url() {
    return this.Domain.concat("/", this.EndPoint);
  }

  constructor(
    public dbSrv: NgxIndexedDBService,
    public spinner: NgxSpinnerService,
    public http: HttpClient,
    public tCreator: new () => E,
  ) {
    this.CurrentChanged$ = new EventEmitter();
    this.CurrentsChanged$ = new EventEmitter();
  }

  protected mapBases(data: any[]): A[] {
    const array = new Array<A>();
    data.forEach((element: any) => {
      const item = this.mapBase(element);
      array.push(item);
    });
    return array;
  }

  protected mapBase(data: any): A {
    return Object.assign(new this.tCreator(), data);
  }

  create(loader: boolean = true): Promise<ResponseResult<E, A>> {
    return this.httpGet(this.Url.concat("/create"), null, true, loader);
  }

  list(filter?: F, loader: boolean = true): Promise<ResponseResult<E, A>> {
    return this.httpPost(this.Url.concat("/list"), filter, true, loader);
  }

  canList(filter?: F, loader: boolean = true): Promise<InformationResult<F>> {
    return this.httpPost(this.Url.concat("/canlist"), filter, false, loader);
  }

  get(
    id: number | string,
    loader: boolean = true,
  ): Promise<ResponseResult<E, A>> {
    if (typeof id === "number") {
      return this.httpGet(this.Url.concat("/get"), { id }, true, loader);
    } else {
      return this.httpGet(
        this.Url.concat("/get"),
        { idGuid: id },
        true,
        loader,
      );
    }
  }

  insert(entity: E, loader: boolean = true): Promise<ResponseResult<E, A>> {
    return this.httpPut(this.Url.concat("/insert"), entity, true, loader);
  }

  update(entity: E, loader: boolean = true): Promise<ResponseResult<E, A>> {
    return this.httpPatch(this.Url.concat("/update"), entity, true, loader);
  }

  abort(type: string) {
    if (this.CurrentCall) {
      const finded = this.CurrentCall.filter((t) => t.Type == type);
      if (finded) {
        finded.forEach((s) => {
          s.Value.unsubscribe();
          let loaderCount = parseInt(
            localStorage.getItem("loader_counter") || "0",
            10,
          );
          if (s.Loader) {
            loaderCount--;
            if (loaderCount < 0) {
              loaderCount = 0;
            }
            localStorage.setItem("loader_counter", loaderCount + "");
            console.log(
              `[Spinner] Abort hide requested by ${this.Url} (type: ${type}) at ${new Date().toISOString()}. Current loaderCount: ${loaderCount}`,
            );
          }
          if (loaderCount <= 0) {
            this.spinner.hide();
          }
        });
      }
    }
  }

  canDelete(
    id: number | string,
    loader: boolean = false,
  ): Promise<BaseResponseResult<boolean>> {
    if (typeof id === "number") {
      return this.httpGet(this.Url.concat("/candelete"), { id }, true, loader);
    } else if (typeof id === "string") {
      return this.httpGet(
        this.Url.concat("/candelete"),
        { idGuid: id },
        true,
        loader,
      );
    }
  }

  delete(
    id: number | string,
    loader: boolean = true,
  ): Promise<ResponseResult<E, A>> {
    if (typeof id === "number") {
      return this.httpDelete(this.Url.concat("/delete"), { id }, true, loader);
    } else if (typeof id === "string") {
      return this.httpDelete(
        this.Url.concat("/delete"),
        { idGuid: id },
        true,
        loader,
      );
    }
  }

  upload(
    files: FileList,
    loader: boolean = true,
  ): Promise<ResponseResult<E, A>> {
    return this.httpUpload(this.Url.concat("/upload"), files, true, loader);
  }

  virtualPath(
    item: E,
    loader: boolean = true,
  ): Promise<BaseResponseResult<string>> {
    return this.httpPost(this.Url.concat("/virtualpath"), item, false, loader);
  }

  thumbs(
    parameters: ImageParameters,
    loader: boolean = true,
  ): Promise<BaseResponseResult<string>> {
    return this.httpPost(this.Url.concat("/thumbs"), parameters, false, loader);
  }

  store(element: E | A): Promise<boolean> {
    return this.storeMany([element]);
  }

  storeMany(element: (E | A)[]): Promise<boolean> {
    this.StoreKey = this.EndPoint.concat("_STORE");
    return new Promise((resolve, reject) => {
      window.localStorage.setItem(this.StoreKey, JSON.stringify(element));
      this.CurrentsChanged$.emit(element);
      this.CurrentChanged$.emit(element[0]);
      return resolve(true);
    });
  }

  remove(): Promise<boolean> {
    this.StoreKey = this.EndPoint.concat("_STORE");
    return new Promise((resolve, reject) => {
      window.localStorage.removeItem(this.StoreKey);
      this.CurrentsChanged$.emit(null);
      this.CurrentChanged$.emit(null);
      return resolve(true);
    });
  }

  clear(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      window.localStorage.clear();
      return resolve(true);
    });
  }

  current(): Promise<E> {
    this.cleanCache();
    return new Promise((resolve, reject) => {
      this.currents().then((r) => {
        if (r && r.length) {
          return resolve(r[0]);
        } else {
          return resolve(null);
        }
      });
    });
  }

  currents(): Promise<E[]> {
    this.StoreKey = this.EndPoint.concat("_STORE");
    return new Promise((resolve, reject) => {
      const data = JSON.parse(
        window.localStorage.getItem(this.StoreKey),
      ) as E[];
      if (!data || !data.length) {
        return resolve([]);
      }
      const items = data.map((t) => Object.assign(new this.tCreator(), t));
      if (!items) {
        return resolve([]);
      } else {
        return resolve(items);
      }
    });
  }

  protected makeCall<T extends IBaseResponseResult<A>>(
    type: Method,
    url: string,
    parameters: any = null,
    map: boolean = true,
    loader: boolean = true,
  ): Promise<void | T> {
    const that = this;
    if (loader) {
      let loaderCount = parseInt(
        localStorage.getItem("loader_counter") || "0",
        10,
      );
      loaderCount++;
      localStorage.setItem("loader_counter", loaderCount + "");
      console.log(
        `[Spinner] Show requested by ${url} at ${new Date().toISOString()}. Current loaderCount: ${loaderCount}`,
      );
      if (loaderCount === 1) {
        that.spinner.show();
      }
    }
    return new Promise<void | T>((resolve, reject) => {
      const startTime = new Date().getTime();
      const onSuccess = (response: T) => {
        if (response && response.Success) {
          let cache: ResponseCache = new ResponseCache({ Success: false });
          if (this.FromCache) {
            cache = JSON.parse(
              window.localStorage.getItem(url + JSON.stringify(parameters)) ||
                "{}",
            );
          }
          if (this.UseCache && !cache.Success) {
            const responsecache: ResponseCache = new ResponseCache({
              Data: new Date(),
              Result: JSON.stringify(response),
              Success: true,
            });
            if ((new Date().getTime() - startTime) / 1000 > 5) {
              window.localStorage.setItem(
                url + JSON.stringify(parameters),
                JSON.stringify(responsecache),
              );
            }
          }

          if (map) {
            if (response.ObjectResult) {
              response.ObjectResult = that.mapBase(response.ObjectResult);
            }
            if (response.ListResult) {
              response.ListResult = that.mapBases(response.ListResult);
            }
          }
        } else {
          response = {
            Success: false,
            ObjectResult: {},
            Filter: null,
            ListResult: [],
            Errors: (response ? response.Errors : {}) || {},
          } as T;
          if (Object.keys(response.Errors || {}).length === 0) {
            response.Errors["Server"] = new Error({ Message: "Server error" });
          }
        }
        return resolve(response);
      };
      const onComplete = () => {
        setTimeout(() => {
          let loaderCount = parseInt(
            localStorage.getItem("loader_counter") || "0",
            10,
          );
          if (loader) {
            loaderCount--;
            if (loaderCount < 0) {
              loaderCount = 0;
            }
            localStorage.setItem("loader_counter", loaderCount + "");
            console.log(
              `[Spinner] Hide requested by ${url} at ${new Date().toISOString()}. Current loaderCount: ${loaderCount}`,
            );
          }
          if (loaderCount <= 0) {
            that.spinner.hide();
          }
        }, 500);
      };
      const onError = (error: any) => {
        console.error(error);
        const result = new BaseResponseResult<A>({
          Success: false,
          ObjectResult: null,
          Filter: null,
          ListResult: null,
          Errors: { ERROR: error },
        });
        onComplete();
        return resolve(result as T);
      };

      let headers: HttpHeaders = new HttpHeaders();
      if (that.Username && that.Password) {
        headers = headers.append(
          "Authorization",
          "Basic " + btoa(that.Username.concat(":", that.Password)),
        );
      }
      if (this.CustomHeaders && this.CustomHeaders.length > 0) {
        this.CustomHeaders.forEach((i) => {
          headers.append(i.Name, i.Value);
        });
      }
      let currentCall: Subscription;
      switch (type) {
        case Method.GET:
          currentCall = that.http
            .get(url, { headers, params: parameters })
            .subscribe(onSuccess, onError, onComplete);
          break;
        case Method.UPLOAD:
          currentCall = that.http
            .post(url, parameters || {}, { headers })
            .subscribe(onSuccess, onError, onComplete);
          break;
        case Method.POST:
          currentCall = that.http
            .post(url, parameters || {}, { headers })
            .subscribe(onSuccess, onError, onComplete);
          break;
        case Method.DOWNLOAD:
          currentCall = that.http
            .post(url, parameters || {}, { headers })
            .subscribe(onSuccess, onError, onComplete);
          break;
        case Method.DELETE:
          this.invalidateCache();
          currentCall = that.http
            .delete(url, { headers, params: parameters })
            .subscribe(onSuccess, onError, onComplete);
          break;
        case Method.PATCH:
          this.invalidateCache();
          currentCall = that.http
            .patch(url, parameters || {}, { headers })
            .subscribe(onSuccess, onError, onComplete);
          break;
        case Method.PUT:
          this.invalidateCache();
          currentCall = that.http
            .put(url, parameters, { headers })
            .subscribe(onSuccess, onError, onComplete);
          break;
      }
      this.CurrentCall.push({
        Type: url.split("/").reverse()[0],
        Value: currentCall,
        Loader: loader,
      });
    }).catch((error) => console.error(JSON.stringify(error)));
  }

  protected checkCache<T>(
    url: string,
    parameters: any,
    onSuccess: (data) => void,
    onComplete: () => void,
  ): boolean {
    if (!this.UseCache) return false;
    // always to invalidate
    const invalidate = [`api/icnuc/list{"NoArea":true}`];
    invalidate.forEach((t) => {
      window.localStorage.removeItem(t);
    });

    const key = url + JSON.stringify(parameters || {});
    const json = window.localStorage.getItem(key);
    const cache: ResponseCache = JSON.parse(json);
    if (cache && cache.Success && this.FromCache) {
      const end = moment(cache.Data);
      const difference = moment.duration(moment(new Date()).diff(end));
      if (difference.asMinutes() < 5) {
        const response: T = JSON.parse(cache.Result || "{}");
        onSuccess(response);
        onComplete();
        return true;
      } else {
        window.localStorage.removeItem(url + JSON.stringify(parameters));
      }
    }
    return false;
  }

  protected cleanCache() {
    const now = moment(new Date());
    for (const key in localStorage) {
      if (localStorage[key]) {
        const storedJson = localStorage[key];
        let value: ResponseCache = null;
        try {
          value = JSON.parse(storedJson);
        } catch {}
        if (value && value.Data) {
          const end = moment(value.Data);
          const difference = moment.duration(now.diff(end));
          if (difference.asHours() > 6) {
            localStorage.removeItem(key);
          }
        }
      }
    }
  }

  protected invalidateCache() {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      const currentEndpoint = key.indexOf("/".concat(this.EndPoint, "/")) > -1;
      let relatedEndpoint = false;

      relatedEndpoint =
        this.EndPoint.indexOf("command") > -1 && key.indexOf("content") > -1;
      relatedEndpoint = relatedEndpoint || this.EndPoint.indexOf("thumbs") > -1;
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("command") > -1 && key.indexOf("command") > -1);
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("command") > -1 && key.indexOf("folder") > -1);
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("command") > -1 && key.indexOf("content") > -1);
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("content") > -1 && key.indexOf("folder") > -1);
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("content") > -1 && key.indexOf("command") > -1);
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("content") > -1 && key.indexOf("content") > -1); // remove all contents in case a single is deleted
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("content") > -1 && key.indexOf("template") > -1);
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("schedule") > -1 &&
          key.indexOf("calendar") > -1);
      relatedEndpoint =
        relatedEndpoint ||
        (this.EndPoint.indexOf("template") > -1 && key.indexOf("content") > -1);
      if ((currentEndpoint || relatedEndpoint) && !key.endsWith("_filter")) {
        window.localStorage.removeItem(key);
        i--;
      }
    }
    this.currents().then((items) => {
      this.CurrentsChanged$.emit(items);
    });
  }

  protected httpUpload(
    url: string,
    files: FileList,
    map: boolean,
    loader = true,
  ): Promise<any> {
    const parameters = new FormData();
    for (let i = 0; i < files.length; i++) {
      const file = files.item(i);
      parameters.append(file.name, file);
    }
    return this.makeCall<ResponseResult<E, A>>(
      Method.UPLOAD,
      url,
      parameters,
      map,
      loader,
    );
  }

  protected httpDownload(
    url: string,
    parameters = null,
    loader = true,
  ): Promise<any> {
    return this.makeCall<ResponseResult<E, A>>(
      Method.DOWNLOAD,
      url,
      parameters,
      false,
      loader,
    );
  }

  protected httpGet(
    url,
    parameters = null,
    map = true,
    loader = true,
  ): Promise<any> {
    return this.makeCall<ResponseResult<E, A>>(
      Method.GET,
      url,
      parameters,
      map,
      loader,
    );
  }

  protected httpPatch(
    url,
    parameters = null,
    map = true,
    loader = true,
  ): Promise<any> {
    return this.makeCall<ResponseResult<E, A>>(
      Method.PATCH,
      url,
      parameters,
      map,
      loader,
    );
  }

  protected httpPut(
    url,
    parameters = null,
    map = true,
    loader = true,
  ): Promise<any> {
    return this.makeCall<ResponseResult<E, A>>(
      Method.PUT,
      url,
      parameters,
      map,
      loader,
    );
  }

  protected httpDelete(
    url,
    parameters = null,
    map = true,
    loader = true,
  ): Promise<any> {
    return this.makeCall<ResponseResult<E, A>>(
      Method.DELETE,
      url,
      parameters,
      map,
      loader,
    );
  }

  protected httpPost(
    url,
    parameters = null,
    map = true,
    loader = true,
  ): Promise<any> {
    return this.makeCall<ResponseResult<E, A>>(
      Method.POST,
      url,
      parameters,
      map,
      loader,
    );
  }

  protected openWindowWithPost(url, data) {
    const form = document.createElement("form");
    form.target = "_blank";
    form.method = "POST";
    form.action = url;
    form.style.display = "none";

    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        if (Array.isArray(data[key])) {
          data[key].forEach((element) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = element;
            form.appendChild(input);
          });
        } else {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = data[key];
          form.appendChild(input);
        }
      }
    }

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }
}
